import { useState } from 'react'
import { Plus } from 'lucide-react'

import { Field } from '../../components/Field'
import type { Reisestart } from '../../lib/trip'
import { AddressSearch } from './AddressSearch'
import {
  entwurfAus,
  koordinatenSchluessel,
  pruefeEntwurf,
  type KoordinatenEntwurf,
} from './platzKoordinate'

/**
 * Der Weg zur ersten Etappe einer leeren Reise - und die Frage, die dabei
 * beantwortet werden muss: wo beginnt die Reise?
 *
 * Hier stand ein Knopf, und die Etappe dahinter begann in Bern. Das war der
 * Startpunkt der ersten Reise, für die diese App gebaut wurde, und solange jede
 * neue Reise mit deren sechzehn Etappen begann, ist es niemandem aufgefallen.
 * Seit eine Reise leer angelegt werden kann, war es ein Blocker: Basel -
 * Münchenbuchsee sind hundert Kilometer und wurden als zehn geplant, weil
 * gerechnet wurde, wie weit es von Bern ist. Niemand konnte es berichtigen, denn
 * ein Feld für den Start gab es nirgends.
 *
 * Gefragt wird deshalb vor dem Anlegen und nicht danach. Jede weitere Etappe
 * übernimmt ihren Start vom Ziel der vorherigen - dort ist nichts zu fragen und
 * nichts zu erfinden. Nur die erste hat nichts, woran sie anknüpfen könnte, und
 * die einzige ehrliche Fassung davon ist: sie entsteht erst, wenn der Punkt
 * feststeht. Ein Platzhalter, der später berichtigt werden darf, wäre wieder ein
 * Ort, den niemand gewählt hat - nur diesmal mit einem Feld daneben, das ihn
 * bestätigt aussehen lässt.
 *
 * Diese Maske steht nur über einer leeren Reise, und das ist eine Voraussetzung
 * und keine Zierde: sie fragt, wo die Reise beginnt, und diese Frage gibt es
 * genau einmal. Sie kann während des Ausfüllens wegfallen, wenn der Abgleich
 * Etappen von einem anderen Gerät bringt - dann beginnt die Reise dort, wo deren
 * erste Etappe beginnt, und der hier eingetippte Punkt gehört an keine Stelle
 * mehr. Angelegt wird dann nichts (insertStageAfter in lib/trip.ts); gesagt wird
 * es in StagesView, denn diese Maske ist im selben Augenblick verschwunden.
 *
 * Die beiden Zahlenfelder führen einen eigenen Entwurf und werden erst beim
 * Anlegen gelesen (pruefeEntwurf) - dieselbe Regel wie bei der Koordinate eines
 * Platzes: „46.36“ ist nach dem ersten Tastendruck erst eine 4, und ein leeres
 * Feld wäre über Number('') der Äquator.
 */
export function ErsteEtappe({ onAnlegen }: { onAnlegen: (start: Reisestart) => void }) {
  const [entwurf, setEntwurf] = useState<KoordinatenEntwurf>({ lat: '', lon: '' })
  const [ort, setOrt] = useState('')

  const befund = pruefeEntwurf(entwurf)

  return (
    <section className="inhalt-block kein-druck">
      <h4 className="block-titel">Wo beginnt die Reise?</h4>

      <AddressSearch
        beschriftung="Startadresse suchen"
        platzhalter="Ort oder Adresse, z. B. Dornacherstrasse 210, Basel"
        onPick={(at, treffer) => {
          setEntwurf(entwurfAus(koordinatenSchluessel(at)))
          // Der lange Name aus der Suche entscheidet den Treffer, taugt aber
          // nicht als Beschriftung einer Etappe: „Immeditech AG,
          // Dornacherstrasse 210, 4053 Basel, Schweiz“ steht sonst im Kopf der
          // Etappenkarte und im Buch. Das erste Stück ist ein Vorschlag, und
          // das Feld darunter bleibt offen.
          if (ort.trim() === '') setOrt(treffer.name.split(',')[0]?.trim() ?? '')
        }}
      />

      <div className="raster-2">
        <Field label="Start Breite">
          <input
            type="number"
            inputMode="decimal"
            step={0.00001}
            value={entwurf.lat}
            placeholder="z. B. 47.541286"
            onChange={(e) => {
              setEntwurf({ ...entwurf, lat: e.target.value })
            }}
          />
        </Field>
        <Field label="Start Länge">
          <input
            type="number"
            inputMode="decimal"
            step={0.00001}
            value={entwurf.lon}
            placeholder="z. B. 7.594486"
            onChange={(e) => {
              setEntwurf({ ...entwurf, lon: e.target.value })
            }}
          />
        </Field>
      </div>

      <div className="raster-2">
        <Field label="Ort">
          <input
            value={ort}
            placeholder="z. B. Basel"
            onChange={(e) => {
              setOrt(e.target.value)
            }}
          />
        </Field>
      </div>

      {befund.art === 'unvollstaendig' && (
        <p className="karte-warnung">
          Ein Feld ist leer. Angelegt wird erst, wenn beide eine Zahl tragen.
        </p>
      )}
      {befund.art === 'unbrauchbar' && (
        <p className="karte-warnung">Diese beiden Zahlen ergeben keinen Ort auf der Erde.</p>
      )}

      <div className="knopfreihe">
        <button
          type="button"
          className="knopf"
          disabled={befund.art !== 'setzen'}
          onClick={() => {
            if (befund.art !== 'setzen') return
            onAnlegen({ at: befund.at, ort })
          }}
        >
          <Plus size={14} /> Erste Etappe anlegen
        </button>
      </div>

      <p className="abschluss">
        Die erste Etappe beginnt hier und endet zunächst am selben Punkt — eine Fahrt wird sie erst
        mit einem Ziel, und erst dann gibt es eine Strecke zu berechnen. Jede weitere Etappe beginnt
        dort, wo die vorherige endet.
      </p>
    </section>
  )
}
